import React from "react";
import BlogImage from "../assets/blognew1.png";

const features = [
  {
    title: "Designed for mobile-first customers",
    desc: "Your customers already use their phones to decide, browse, and order. Zafflo’s digital menus are fast, responsive, and optimized for real-world mobile usage — even on slow networks.",
  },
  {
    title: "No apps, no friction",
    desc: "Customers don’t need to install another app or create an account. A simple QR scan opens your menu instantly.",
  },
  {
    title: "WhatsApp-native ordering",
    desc: "Orders are delivered directly to WhatsApp — familiar, reliable, and easy for staff to manage.",
  },
  {
    title: "Works across dine-in, delivery & bookings",
    desc: "From table ordering to advance bookings, Zafflo adapts to how your business already works.",
  },
  {
    title: "Instant menu updates",
    desc: "Update prices, availability, or items in seconds without reprinting menus.",
  },
  {
    title: "Visual menus that increase order value",
    desc: "Images and structured layouts encourage customers to explore more and order confidently.",
  },
  {
    title: "Professional QR everywhere",
    desc: "Clean QR codes for tables, counters, rooms, bills, and packaging.",
  },
  {
    title: "Centralized order visibility",
    desc: "Track orders on WhatsApp and dashboard without juggling calls or paper notes.",
  },
];

export default function QrMenuForRestaurants() {
  return (
    <div className="bg-white text-gray-800 overflow-hidden">

      {/* HERO */}
      <section className="relative bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-6 py-28 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="inline-block mb-4 text-sm uppercase tracking-wider text-[#6cbb23]">
              QR Menus & Online Ordering
            </span>

            <h1 className="text-4xl md:text-5xl font-bold leading-tight">
              The fastest way for customers to order — and businesses to grow
            </h1>

            <p className="mt-6 text-lg text-gray-300">
              Zafflo replaces printed menus, phone calls, and manual order taking
              with a simple QR-powered digital experience — built around WhatsApp.
            </p>

            <p className="mt-4 text-gray-300">
              Whether you run a restaurant, café, hotel, or service business,
              Zafflo helps you serve more customers with less effort.
            </p>

            <div className="mt-10 flex gap-4">
              <a
                href="/signup"
                className="rounded-xl bg-[#6cbb23] px-6 py-3 font-semibold text-white hover:opacity-90"
              >
                Get Started Free
              </a>
              <a
                href="/demo"
                className="rounded-xl border border-white/30 px-6 py-3 font-semibold text-white hover:bg-white/10"
              >
                View Demo
              </a>
            </div>

            <p className="mt-4 text-sm text-gray-400">
              No credit card required · Live in under 5 minutes
            </p>
          </div>

          <div className="hidden lg:block">
            <img
              src={BlogImage}
              alt="QR Menu and Online Ordering"
              className="w-full max-w-md mx-auto drop-shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* REALITY SECTION */}
      <section className="py-28 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center text-gray-900">
            The reality of manual ordering today
          </h2>

          <div className="mt-14 rounded-3xl bg-white p-12 shadow-lg">
            <ul className="space-y-6">
              {[
                "Printed menus become outdated and expensive to maintain",
                "Peak-hour rush exposes staff dependency",
                "Phone orders lead to mistakes and missed items",
                "Customers wait longer to place simple orders",
                "No structured customer data for repeat business",
                "Owners lack real-time visibility into orders",
              ].map((item, idx) => (
                <li key={idx} className="flex gap-4">
                  <span className="mt-2 h-2 w-2 rounded-full bg-[#6cbb23]" />
                  <p className="text-gray-700">{item}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CUSTOMER FLOW */}
      <section className="py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center">
            A simple flow customers already understand
          </h2>

          <div className="mt-20 grid md:grid-cols-4 gap-10">
            {[
              ["Scan QR", "Customer scans the QR code"],
              ["Browse", "Views menu or services"],
              ["Order", "Places order in seconds"],
              ["Receive", "Staff gets order on WhatsApp"],
            ].map((step, idx) => (
              <div key={idx} className="text-center">
                <div className="mx-auto h-12 w-12 rounded-full bg-[#6cbb23]/10 text-[#6cbb23] flex items-center justify-center font-bold">
                  {idx + 1}
                </div>
                <h3 className="mt-4 font-semibold">{step[0]}</h3>
                <p className="mt-2 text-sm text-gray-600">{step[1]}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHATSAPP ADVANTAGE */}
      <section className="py-28 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold">
            Powered by WhatsApp — not another system
          </h2>

          <p className="mt-6 text-gray-300 max-w-3xl mx-auto">
            Zafflo works with tools your staff already uses. No training-heavy
            dashboards, no complicated POS integrations.
          </p>

          <div className="mt-16 grid md:grid-cols-3 gap-10">
            {[
              "Instant order notifications",
              "Easy staff coordination",
              "Lower training time",
              "Faster response to customers",
              "Familiar & trusted platform",
            ].map((item, idx) => (
              <div
                key={idx}
                className="rounded-2xl bg-white/5 p-6"
              >
                <p className="text-gray-200">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center">
            Built for real-world business needs
          </h2>

          <div className="mt-20 grid md:grid-cols-3 gap-12">
            {features.map((f, i) => (
              <div key={i}>
                <h3 className="font-semibold text-lg">{f.title}</h3>
                <p className="mt-3 text-gray-600">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

          {/* CTA */}
      <section className="py-28 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold">
            Start serving customers the modern way
          </h2>
          <p className="mt-4 text-gray-600">
            Replace manual ordering with a faster, cleaner, and smarter system.
          </p>

          <a
            href="/signup"
            className="mt-8 inline-block rounded-xl bg-[#6cbb23] px-8 py-4 text-white font-semibold"
          >
            Start Free with Zafflo
          </a>
        </div>
      </section>
    </div>
  );
}
